import { AnimationEvent } from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import { ApplicationRef, Component, DestroyRef, inject, OnInit, Optional, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { IconService, pageTransition, RouteService } from 'app';
import { interval, lastValueFrom } from 'rxjs';
import { delay, filter, first, map, startWith, take, tap } from 'rxjs/operators';
import { Logger, ToastContainerComponent, ToastService } from 'shared';

import {
  ButtonDirective,
  ColorModeService,
  ModalModule,
  ToastBodyComponent,
  ToastCloseDirective,
  ToastComponent,
  ToasterComponent
} from '@coreui/angular-pro';

import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { MatomoTrackerService } from 'analytics';
import { AppInstanceService } from 'app';
import { AuthService } from 'auth';
import { TranslateDirective, TranslateService } from 'i18n';
import { Platform } from 'shared';

@Component({
  selector: 'app-root',
  template: `
    <div
      [style.opacity]="opacity"
      [@pageTransition]="animationState"
      (@pageTransition.start)="onPageAnimationStart($event)"
      (@pageTransition.done)="onPageAnimationDone($event)">
      <router-outlet #outlet="outlet"/>
    </div>
    <c-toaster placement="bottom-end" position="fixed">
      <c-toast *ngIf="displayUpdateToast()"
              #toast="cToast" 
              [visible]="true"
              [autohide]="false" 
              class="align-items-center 
              text-white border-0" 
              color="primary">
        <div class="d-flex">
          <c-toast-body>
            <span>A new version is available. Update now?</span>
            <div class="mt-2 pt-2 border-top">
              <button [cToastClose]="toast" 
                      cButton 
                      class="ms-1" 
                      color="secondary" 
                      size="sm" 
                      (click)="displayUpdateToast.set(false)">
                Close
              </button>
              <button cButton 
                      color="primary" 
                      size="sm" 
                      (click)="onAppUpdate()">
                Update
              </button>
            </div>
          </c-toast-body>
        </div>
      </c-toast>
      <toast-container></toast-container>
    </c-toaster>
    <c-modal *ngIf="displayInstanceModal()" 
      #instanceModal
      id="verticallyCenteredModal"
      alignment="center"
      backdrop="static"
      [visible]="true"
      >
      <c-modal-header>
        <h5 cModalTitle translate="app.name"></h5>
        <!--button [cModalToggle]="instanceModal.id" cButtonClose></button-->
      </c-modal-header>
      <c-modal-body>
        You are signed in with a different user account in another tab.
      </c-modal-body>
      <c-modal-footer>
        <button [cModalToggle]="instanceModal.id" cButton color="primary" (click)="onAppClose()" >
          Close this Tab
        </button>
      </c-modal-footer>
    </c-modal>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
      xpadding-top: env(safe-area-inset-top);
      xbackground-color: #fff; /* Ensure background fills notch area */
    }
    div:first-child {
      position: relative;
      height: 100%;
    }
    c-toaster {
      bottom: 10px !important;
      right: 10px !important;
      width: auto;
    }
    c-toast { width: auto; }
    button { float: right; }
  `,
  imports: [CommonModule, RouterOutlet, ToastContainerComponent,
            ToasterComponent, ToastComponent, ToastBodyComponent, ToastCloseDirective,
            ButtonDirective, ModalModule, TranslateDirective],
  animations: [
    pageTransition
  ],
  standalone: true,
})
export class AppComponent implements OnInit {

  outlet  = viewChild.required<RouterOutlet>('outlet');
  toaster = viewChild<ToasterComponent>(ToasterComponent);

  destroyRef: DestroyRef = inject(DestroyRef);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  titleService = inject(Title);

  iconService = inject(IconService);
  colorModeService = inject(ColorModeService);
  
  displayUpdateToast = signal(false);
  displayInstanceModal = signal(false);

  opacity = 0;
  animationState = 'void';

  protected logger = new Logger('ApplicationComponent');
  
  constructor(private router: Router,
              private routeService: RouteService,
              @Optional() protected sw: SwUpdate,
              private appInstanceService: AppInstanceService,
              private authService: AuthService,
              private screenOrientation: ScreenOrientation,
              private statusBar: StatusBar,
              private platform: Platform,
              private applicationRef: ApplicationRef,
              @Optional() private matomoTracker: MatomoTrackerService,
              public toastService: ToastService,
              protected translateService: TranslateService) {
    this.logger.log('AppComponent.ctor()');

    this.titleService.setTitle(this.title);
    this.iconService.initializeIcons();

    //this.colorModeService.localStorageItemName.set('coreui-pro-angular-admin-template-theme-light');
    //this.colorModeService.eventName.set('ColorSchemeChange');
    // this.colorModeService.colorMode.set('dark');
    
    // sw?.versionUpdates.pipe(takeUntilDestroyed()).subscribe(evt => {
    //   if (evt.type === 'VERSION_READY') {
    //     console.info("======== NEW VERSION AVAILABLE ========");
    //     this.displayUpdateToast.set(true);
    //   }
    // });
  }

  ngOnInit(): void {
    this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          tap((event: NavigationEnd) => {
            const animationState = this.outlet().activatedRouteData?.['animation'] || 'initialized';
            if (animationState && this.animationState != animationState) {
              this.animationState = animationState;
              this.logger.debug('Animation state update', this.animationState);
            }

            // Track page view in Matomo
            if (this.matomoTracker) {
              this.matomoTracker.trackPageView({
                customUrl: event.urlAfterRedirects,
                documentTitle: this.titleService.getTitle()
              });
            }
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();

    this.activatedRoute.queryParams
        .pipe(
          delay(1),
          map(params => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
          filter(theme => ['dark', 'light', 'auto'].includes(theme)),
          tap(theme => {
            this.colorModeService.colorMode.set(theme);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();

    this.appInstanceService
        .instanceDetected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({detected, userId}) => {
          if (detected && userId!=this.authService.getUser()?.id) {
            if (/^\/[a-zA-Z0-9-]+\/chat/i.test(this.router.url ?? '')) {
              // get out of chat page to relase IndexedDB locks and allow 
              // the other instance (user) to reinitialize the chat
              this.router.navigate([this.routeService.getDefaultUrl()]);
            }
            this.displayInstanceModal.set(true);
          }
        });

    this.platform.ready()
      // .then(readySource => this.bootstrap())
      .then(() => {
        // this.logger.debug('APP BOOTSTRAP DONE');
        lastValueFrom(this.applicationRef.isStable.pipe(first(isStable => isStable === true)));
      })
      .then(() => {
        if (this.sw?.isEnabled) {
          interval(2 * 60 * 1000)
            .pipe(takeUntilDestroyed(this.destroyRef), startWith(0))
            .subscribe(() => {
              this.sw.checkForUpdate();
            });
            
          this.sw.versionUpdates
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(evt => {
            if (evt.type === 'VERSION_READY') {
              this.logger.info("======== NEW VERSION AVAILABLE ========");
              this.displayUpdateToast.set(true);
            }
          });

        }
        if (this.platform.is('hybrid')) {
          if (!this.platform.is('tablet')) {
            this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT_PRIMARY);
          }
          // env(safe-area-inset-*) variables are not supported in android webview?
          this.statusBar.overlaysWebView(this.platform.is('ios'));
          this.statusBar.styleLightContent(); //styleDefault(); //dark text, for light backgrounds
          (navigator as any).splashscreen.hide();
          // this.router.events
          // .pipe(
          //   filter(event => event instanceof NavigationEnd),
          //   take(1),
          // )
          // .subscribe(event => {
          //   this.deeplinks.route({
          //     '/sso/:token'    : 'login', // for login from dash
          //     '/login/:token'  : 'login',
          //     '/chat/:roomId'  : 'chat'   // for later usage
          //   }).subscribe({ 
          //     next: (match) => {
          //       // match.$route - the route we matched, which is the matched entry from the arguments to route()
          //       // match.$args - the args passed in the link
          //       // match.$link - the full link data
          //       // example:
          //       // $route: "join"
          //       // $args: {invitationCode: "7PKBG-7YBL5-AXGQ5-6PYGJ-Y9NPN"}
          //       // $link: {path: "/join/7PKBG-7YBL5-AXGQ5-6PYGJ-Y9NPN", queryString: "", fragment: "", host: "www.realizer.app", url: "https://www.realizer.app/join/7PKBG-7YBL5-AXGQ5-6PYGJ-Y9NPN", …}

          //       // window.alert(`DEEPLINK ${JSON.stringify(match)}`);
          //       this.logger.info('Deeplink match', match);
          //       const action: string = match.$route;
          //       const isLoggedIn = this.authService.isLoggedIn();
          //       const url = match.$link?.path || '/';
          //       // if (isLoggedIn) {
          //         if (this.router.url!=url) {
          //           this.router.navigateByUrl(url);
          //         }
          //       // } else {
          //       // }
          //     }, 
          //     error: (nomatch) => {
          //       // nomatch.$link - the full link data
          //       this.logger.warn('Deeplink didn\'t match', nomatch);
          //     }
          //   })
          // });
        }
        this.router.initialNavigation();
      })
      .catch(error => this.logger.error('BOOTSTRAP ERROR', error));
  }

  onAppUpdate() {
    window.location.reload();
  }

  onAppClose(): void {
    window.location.href = 'about:blank';
  }

  onPageAnimationStart(event: AnimationEvent) {
    this.logger.debug('onPageAnimationStart', event);
    this.opacity = 1;
  }

  onPageAnimationDone(event: AnimationEvent) {
    this.logger.debug('onPageAnimationDone', event);
  }

  get title() {
    return this.translateService.translate('app.name');
  }
}
